import { useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import useEffiencyInsightsColumns from '../../hooks/useEffiencyInsightsColumns';
import { EfficiencyInsightsSorting, FilterDimension } from '../../schemas';
import { EfficiencyInsight, EfficiencyKpiType } from '../../types';

import usePlatformTraffickingLandingRoute from '@/features/navigation/hooks/usePlatformTraffickingLandingRoute';
import { usePlatform } from '@/features/users';
import { Table } from '@/shared/components/table';

interface EffiencyInsightsTableProps {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  data: EfficiencyInsight[];
  dataCount: number;
  efficiencyCategory: FilterDimension;
}

const EfficiencyInsightsTable = ({
  isLoading,
  isFetching,
  isError,
  data,
  dataCount,
  efficiencyCategory,
}: EffiencyInsightsTableProps) => {
  const traffickingLink = usePlatformTraffickingLandingRoute();
  const platform = usePlatform();

  const { effPage, effPageSize, efficiencyInsightsOrdering } = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });
  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (!efficiencyInsightsOrdering) {
      return [];
    }

    const isDesc = efficiencyInsightsOrdering.startsWith('-');
    const fieldName = isDesc
      ? efficiencyInsightsOrdering.substring(1)
      : efficiencyInsightsOrdering;

    return [
      {
        id: fieldName,
        desc: isDesc,
      },
    ];
  });

  const kpiType: EfficiencyKpiType =
    data[0]?.kpiType ?? (platform === 'Meta' ? 'CPV' : 'CPM');
  const columns = useEffiencyInsightsColumns(efficiencyCategory, kpiType);
  const table = useReactTable({
    state: {
      pagination: {
        pageIndex: effPage - 1,
        pageSize: effPageSize,
      },
      sorting,
    },
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    manualSorting: true,
    enableRowSelection: false,
    manualPagination: true,
    rowCount: dataCount,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater(table.getState().pagination)
          : updater;

      void navigate({
        search: (old) => ({
          ...old,
          effPage: newPagination.pageIndex + 1,
          effPageSize: newPagination.pageSize,
        }),
        resetScroll: false,
      });
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);

      const orderingMap: Record<
        string,
        Exclude<EfficiencyInsightsSorting, `-${string}`>
      > = {
        cpm: 'cpm',
        cpv: 'cpv',
        categoryName: 'category_name',
        efficiencyIndex: 'efficiency_index',
        ranking: 'ranking',
      };

      let ordering = undefined;

      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        ordering = orderingMap[id];
        if (desc && ordering) {
          ordering = `-${ordering}`;
        }
      }

      void navigate({
        search: (prev) => ({
          ...prev,
          efficiencyInsightsOrdering: ordering,
          page: 1,
        }),
        resetScroll: false,
      });
    },
  });

  return (
    <Table
      table={table}
      isError={isError}
      isFetching={isFetching}
      isLoading={isLoading}
      tableLabel='Efficiency Insights Table'
      emptyHeading='No insights to show'
      emptyText='Start optimizing using Starvation Bidder to see insights here.'
      errorText='Failed to load efficiency insights.'
      ctaText='Go to Trafficking'
      ctaLink={traffickingLink}
    />
  );
};

export default EfficiencyInsightsTable;
